import React, { useEffect, useState } from 'react';
import { UserPlus, Trash2, KeyRound, BookOpen, Plus, X, Save, Loader2, Upload, Video as VideoIcon, FileText } from 'lucide-react';
import { portalApi, formatApiError } from '../../contexts/PortalAuthContext';
import { toast } from 'sonner';

export default function PortalAdmin() {
  const [tab, setTab] = useState('students');
  return (
    <div data-testid="portal-admin">
      <header className="mb-6">
        <div className="text-rose-400 text-xs uppercase tracking-widest mb-2">Admin</div>
        <h1 className="text-3xl font-semibold text-white">Programme Admin</h1>
        <p className="text-slate-400 mt-2">Manage students and module content.</p>
      </header>

      <div className="flex gap-2 border-b border-slate-800 mb-6">
        <TabBtn label="Students" active={tab==='students'} onClick={() => setTab('students')} testId="admin-tab-students" />
        <TabBtn label="Modules" active={tab==='modules'} onClick={() => setTab('modules')} testId="admin-tab-modules" />
      </div>

      {tab === 'students' && <StudentsTab />}
      {tab === 'modules' && <ModulesTab />}
    </div>
  );
}

function TabBtn({ label, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-4 py-2.5 text-sm font-medium transition border-b-2 -mb-px ${active ? 'text-white border-amber-500' : 'text-slate-400 border-transparent hover:text-white'}`}
    >
      {label}
    </button>
  );
}

// ---------------- Students ----------------
function StudentsTab() {
  const [students, setStudents] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', full_name: '', cohort: '', role: 'student' });

  const load = async () => {
    try { const { data } = await portalApi.get('/admin/students'); setStudents(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await portalApi.post('/admin/students', form);
      toast.success('Student created');
      setForm({ email: '', password: '', full_name: '', cohort: '', role: 'student' });
      setCreating(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this user, their progress, and journal entries?')) return;
    try {
      await portalApi.delete(`/admin/students/${id}`);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resetPw = async (id) => {
    try {
      const { data } = await portalApi.post(`/admin/students/${id}/reset-password`);
      window.prompt('New password (copy & send to student):', data.new_password);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section>
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-slate-400">{students.length} member{students.length !== 1 && 's'}</div>
        <button onClick={() => setCreating(true)} className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 text-sm font-semibold rounded-lg px-4 py-2" data-testid="admin-new-student-btn">
          <UserPlus className="w-4 h-4" /> Add student
        </button>
      </div>

      <div className="rounded-xl border border-slate-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-900 text-slate-400 text-xs uppercase">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3 hidden md:table-cell">Email</th>
              <th className="text-left p-3 hidden md:table-cell">Cohort</th>
              <th className="text-left p-3">Progress</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {students.map(s => (
              <tr key={s.id} className="border-t border-slate-800">
                <td className="p-3 text-white">
                  {s.full_name}
                  {s.role === 'admin' && <span className="ml-2 text-[10px] uppercase bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded">Admin</span>}
                </td>
                <td className="p-3 text-slate-300 hidden md:table-cell">{s.email}</td>
                <td className="p-3 text-slate-400 hidden md:table-cell">{s.cohort || '—'}</td>
                <td className="p-3 text-slate-300">{s.progress_percent}%</td>
                <td className="p-3 text-right space-x-1">
                  <button onClick={() => resetPw(s.id)} title="Reset password" className="p-1.5 text-slate-400 hover:text-amber-400">
                    <KeyRound className="w-4 h-4" />
                  </button>
                  {s.role !== 'admin' && (
                    <button onClick={() => remove(s.id)} title="Delete" className="p-1.5 text-slate-400 hover:text-rose-400">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4" onClick={() => setCreating(false)}>
          <form onSubmit={create} onClick={e => e.stopPropagation()} className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl p-6" data-testid="admin-new-student-form">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-white">New student</h3>
              <button type="button" onClick={() => setCreating(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="space-y-3">
              <InputField label="Full name" value={form.full_name} onChange={v => setForm({...form, full_name: v})} required testId="student-name" />
              <InputField label="Email" type="email" value={form.email} onChange={v => setForm({...form, email: v})} required testId="student-email" />
              <InputField label="Initial password" value={form.password} onChange={v => setForm({...form, password: v})} required testId="student-password" />
              <InputField label="Cohort label (e.g. July 2026)" value={form.cohort} onChange={v => setForm({...form, cohort: v})} testId="student-cohort" />
            </div>
            <button type="submit" className="mt-5 w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold rounded-lg py-2.5" data-testid="student-create-submit">
              Create student
            </button>
          </form>
        </div>
      )}
    </section>
  );
}

// ---------------- Modules ----------------
function ModulesTab() {
  const [modules, setModules] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await portalApi.get('/modules');
      setModules(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm('Delete this module?')) return;
    try { await portalApi.delete(`/admin/modules/${id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section>
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-slate-400">{modules.length} modules</div>
        <button onClick={() => setEditing({ new: true })} className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 text-sm font-semibold rounded-lg px-4 py-2" data-testid="admin-new-module-btn">
          <Plus className="w-4 h-4" /> Add module
        </button>
      </div>

      <div className="space-y-3">
        {modules.map(m => (
          <div key={m.id} className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 flex items-start gap-4">
            <div className="text-2xl font-bold text-slate-700 w-10 shrink-0">{String(m.number).padStart(2, '0')}</div>
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-semibold">{m.title}</h3>
              <p className="text-xs text-slate-400 line-clamp-1">{m.subtitle}</p>
              <div className="text-xs text-slate-500 mt-1">
                {m.tasks?.length || 0} tasks · {m.video_file || m.video_url ? '🎬 video' : 'no video'} · {m.workbook_url ? '📘 workbook' : 'no workbook'} · {m.published ? 'published' : 'draft'}
              </div>
            </div>
            <div className="flex flex-col gap-1 shrink-0">
              <button onClick={() => setEditing(m)} className="text-xs text-amber-400 hover:underline" data-testid={`admin-edit-module-${m.number}`}>Edit</button>
              <button onClick={() => remove(m.id)} className="text-xs text-rose-400 hover:underline">Delete</button>
            </div>
          </div>
        ))}
      </div>

      {editing && <ModuleEditor module={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </section>
  );
}

function ModuleEditor({ module: m, onClose, onSaved }) {
  const isNew = !!m.new;
  const [form, setForm] = useState({
    number: isNew ? '' : m.number,
    title: isNew ? '' : m.title || '',
    subtitle: isNew ? '' : m.subtitle || '',
    description: isNew ? '' : m.description || '',
    video_url: isNew ? '' : m.video_url || '',
    video_file: isNew ? '' : m.video_file || '',
    workbook_url: isNew ? '' : m.workbook_url || '',
    duration_minutes: isNew ? 45 : (m.duration_minutes || 45),
    tasks: isNew ? [] : (m.tasks || []),
    resources: isNew ? [] : (m.resources || []),
    published: isNew ? true : (m.published !== false),
  });
  const [saving, setSaving] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingWorkbook, setUploadingWorkbook] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, number: parseInt(form.number, 10), duration_minutes: parseInt(form.duration_minutes, 10) || 0 };
      if (isNew) {
        await portalApi.post('/admin/modules', payload);
      } else {
        await portalApi.put(`/admin/modules/${m.id}`, payload);
      }
      toast.success('Saved');
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const uploadVideo = async (e) => {
    if (isNew) return toast.error('Save module first, then upload video');
    const file = e.target.files?.[0]; if (!file) return;
    setUploadingVideo(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await portalApi.post(`/admin/modules/${m.id}/video`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, video_file: data.video_file }));
      toast.success('Video uploaded');
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploadingVideo(false); }
  };

  const uploadWorkbook = async (e) => {
    if (isNew) return toast.error('Save module first');
    const file = e.target.files?.[0]; if (!file) return;
    setUploadingWorkbook(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await portalApi.post(`/admin/modules/${m.id}/workbook`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, workbook_url: data.workbook_url }));
      toast.success('Workbook uploaded');
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploadingWorkbook(false); }
  };

  const addTask = () => setForm(f => ({ ...f, tasks: [...f.tasks, { id: cryptoRandomId(), title: '', description: '' }] }));
  const removeTask = (i) => setForm(f => ({ ...f, tasks: f.tasks.filter((_, idx) => idx !== i) }));
  const updateTask = (i, key, value) => setForm(f => ({ ...f, tasks: f.tasks.map((t, idx) => idx === i ? { ...t, [key]: value } : t) }));

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4 py-8" onClick={onClose}>
      <form onSubmit={save} onClick={e => e.stopPropagation()} className="w-full max-w-3xl bg-slate-900 border border-slate-800 rounded-2xl p-6 max-h-[92vh] overflow-y-auto" data-testid="admin-module-editor">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-lg font-semibold text-white">{isNew ? 'New module' : `Edit module ${m.number}`}</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <InputField label="Number" type="number" value={form.number} onChange={v => setForm({...form, number: v})} required testId="module-number" />
          <InputField label="Duration (mins)" type="number" value={form.duration_minutes} onChange={v => setForm({...form, duration_minutes: v})} testId="module-duration" />
        </div>
        <InputField label="Title" value={form.title} onChange={v => setForm({...form, title: v})} required testId="module-title" />
        <div className="h-3" />
        <InputField label="Subtitle / tagline" value={form.subtitle} onChange={v => setForm({...form, subtitle: v})} testId="module-subtitle" />
        <div className="h-3" />
        <label className="block text-xs font-medium text-slate-300 mb-1.5">Description</label>
        <textarea
          value={form.description}
          onChange={e => setForm({...form, description: e.target.value})}
          rows={4}
          className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 outline-none resize-y"
          data-testid="module-description"
        />

        {/* Video */}
        <div className="mt-4 rounded-lg border border-slate-800 p-4">
          <div className="flex items-center gap-2 mb-2"><VideoIcon className="w-4 h-4 text-amber-400" /><span className="text-sm font-medium text-white">Video</span></div>
          <InputField label="External URL (YouTube/Vimeo/Bunny — recommended)" value={form.video_url} onChange={v => setForm({...form, video_url: v})} placeholder="https://" testId="module-video-url" />
          <div className="text-center text-xs text-slate-500 my-2">OR upload directly</div>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer text-amber-400 hover:underline">
            {uploadingVideo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploadingVideo ? 'Uploading…' : 'Upload video file'}
            <input type="file" accept="video/*" className="hidden" onChange={uploadVideo} disabled={isNew} data-testid="module-video-upload" />
          </label>
          {form.video_file && <div className="text-xs text-slate-500 mt-2 truncate">📁 {form.video_file}</div>}
          {isNew && <div className="text-xs text-rose-400 mt-2">Save module first, then upload a file.</div>}
        </div>

        {/* Workbook */}
        <div className="mt-3 rounded-lg border border-slate-800 p-4">
          <div className="flex items-center gap-2 mb-2"><FileText className="w-4 h-4 text-sky-400" /><span className="text-sm font-medium text-white">Workbook</span></div>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer text-sky-400 hover:underline">
            {uploadingWorkbook ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploadingWorkbook ? 'Uploading…' : 'Upload PDF/DOCX'}
            <input type="file" accept=".pdf,.docx,.doc" className="hidden" onChange={uploadWorkbook} disabled={isNew} data-testid="module-workbook-upload" />
          </label>
          {form.workbook_url && <div className="text-xs text-slate-500 mt-2 truncate">📁 {form.workbook_url}</div>}
        </div>

        {/* Tasks */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-white">Tasks ({form.tasks.length})</div>
            <button type="button" onClick={addTask} className="text-xs text-amber-400 hover:underline" data-testid="module-add-task">+ Add task</button>
          </div>
          <div className="space-y-2">
            {form.tasks.map((t, i) => (
              <div key={t.id || i} className="flex gap-2 items-start">
                <div className="flex-1 space-y-1.5">
                  <input
                    value={t.title}
                    onChange={e => updateTask(i, 'title', e.target.value)}
                    placeholder={`Task ${i+1} title`}
                    className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-1.5 text-white text-sm focus:border-amber-500 outline-none"
                  />
                  <input
                    value={t.description || ''}
                    onChange={e => updateTask(i, 'description', e.target.value)}
                    placeholder="Description (optional)"
                    className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-1.5 text-slate-400 text-xs focus:border-amber-500 outline-none"
                  />
                </div>
                <button type="button" onClick={() => removeTask(i)} className="p-2 text-slate-400 hover:text-rose-400">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <label className="mt-5 flex items-center gap-2 text-sm text-slate-300">
          <input type="checkbox" checked={form.published} onChange={e => setForm({...form, published: e.target.checked})} className="accent-amber-500" data-testid="module-published" />
          Published (visible to students)
        </label>

        <button
          type="submit"
          disabled={saving}
          className="mt-5 inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold rounded-lg px-4 py-2 disabled:opacity-60"
          data-testid="module-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save module
        </button>
      </form>
    </div>
  );
}

function InputField({ label, value, onChange, required, type='text', testId, placeholder }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-300 mb-1.5">{label}</label>
      <input
        type={type}
        required={required}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 outline-none"
        data-testid={testId}
      />
    </div>
  );
}

function cryptoRandomId() {
  try { return crypto.randomUUID(); }
  catch { return 'id-' + Math.random().toString(36).slice(2); }
}
